import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Small ⓘ info icon with a hover/focus popover. The popover renders in a
// document.body portal (position: fixed) so it is never clipped by the
// scrolling table containers (.home-module-body has overflow:auto).
// `content` is a trusted static HTML string (from tour/copy.ts) so bullets and
// bold render; it is never user-supplied.
export default function InfoTip({
  content,
  label = 'More info',
  size = 14,
}: {
  content: string;
  label?: string;
  size?: number;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 });
  const id = useId();

  const place = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    const pop = popRef.current;
    const pw = pop?.offsetWidth ?? 260;
    const ph = pop?.offsetHeight ?? 80;
    const margin = 8;
    let left = r.left;
    let top = r.bottom + 6;
    if (left + pw > window.innerWidth - margin) left = window.innerWidth - pw - margin;
    if (left < margin) left = margin;
    if (top + ph > window.innerHeight - margin) top = r.top - ph - 6; // flip above
    setPos({ top, left });
  }, []);

  const show = useCallback(() => {
    setOpen(true);
  }, []);
  const hide = useCallback(() => setOpen(false), []);

  // Position after the popover is in the DOM (so we can measure it), and close
  // on scroll/resize so it never drifts away from a scrolled trigger.
  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="infotip-trigger"
        style={{ width: size + 2, height: size + 2, fontSize: size - 2 }}
        aria-label={label}
        aria-describedby={open ? id : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        ⓘ
      </button>
      {open &&
        createPortal(
          <div
            ref={popRef}
            id={id}
            role="tooltip"
            className="infotip-popover"
            style={{ top: pos.top, left: pos.left }}
            dangerouslySetInnerHTML={{ __html: content }}
          />,
          document.body
        )}
    </>
  );
}
